import * as React from 'react';
import { twMerge } from 'tailwind-merge';
import SweetScrollContext from '../../utils/SweetScrollContext';
import { AccordionDetails, AccordionSummary, Accordion as MuiAccordion } from '@mui/material';
import useHash from '../../hooks/useHash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface AccordionProps {
  className?: string;
  id?: string;
  anchorId?: string;
  HeadingComponent?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element | undefined;
  DetailsComponent?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element | undefined;
  CustomExpandIcon?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element | undefined;
  [x: string]: any;
}

const Accordion = ({
  className = '',
  id,
  anchorId,
  HeadingComponent,
  DetailsComponent,
  CustomExpandIcon,
  ...otherProps
}: AccordionProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const [hash] = useHash();
  const { scrollToAnchor } = React.useContext<any>(SweetScrollContext);

  React.useEffect(() => {
    const expand = `#${anchorId || id}` === hash;
    setExpanded(expand);
    if (expand && scrollToAnchor) {
      scrollToAnchor(`#${anchorId || id}`, { updateURL: false });
    }
  }, [hash]);

  return (
    <div className={twMerge('accordion-container', className)} id={id} {...otherProps}>
      <MuiAccordion
        expanded={expanded}
        classes={{
          root: 'accordion !shadow-none border border-solid border-1 border-grey-3 !rounded-[4px]',
        }}
      >
        <AccordionSummary
          classes={{
            root: 'accordion-summary flex !mb-0 !p-0 !min-h-max',
            expandIconWrapper:
              'accordion-summary-icon flex !justify-start !my-0 [&>svg]:fill-[black]',
            content: 'accordion-summary-content !my-0',
            expanded: 'accordion-summary-expanded !my-0',
          }}
          onClick={() => setExpanded(!expanded)}
          expandIcon={CustomExpandIcon ? <CustomExpandIcon /> : <ExpandMoreIcon />}
        >
          {HeadingComponent && <HeadingComponent className="leading-none" />}
        </AccordionSummary>
        <AccordionDetails
          classes={{
            root: 'accordion-details !p-0 !mt-3 !mt-4',
          }}
        >
          {DetailsComponent && <DetailsComponent className="font-md" />}
        </AccordionDetails>
      </MuiAccordion>
    </div>
  );
};

export default Accordion;
